import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Tooltip,
  IconButton,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { createEmployee } from "../../reducers/EmployeeReducer";
import PATCloseButton from "../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../components/Buttons/PATSaveButton";
import moment from "moment";
import { isValidDateTest, mapDictionaryOptions } from "../../utils/functions";
import DatePickerController from "../../components/inputs/DatePickerController";
import TextFieldController from "../../components/inputs/TextFieldController";
import { getService } from "../../reducers/service";
import { PATH_EMPLOYEE, PATH_EMPLOYEE_EDIT } from "../../utils/routeConstants";
import { notify } from "../../reducers/NotifierReducer";
import { entityNamesForCustomId, serviceNames } from "../../utils/constants";
import { AssignmentReturn } from "@mui/icons-material";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import DropdownController from "../../components/inputs/DropdownController";
import { RegularExpressions } from "../../utils/regular-expressions";
import { formatSaveDate } from "../../reducers/TimeReducer";

// Define Yup validation schema
const schema = yup.object().shape({
  employeeNum: yup
    .string()
    .trim()
    .matches(RegularExpressions.ID, "Invalid Employee ID")
    .required("Employee # is required")
    .max(100, "Maximum 100 characters allowed"),
  firstName: yup
    .string()
    .trim()
    .matches(RegularExpressions.Name, "Invalid First Name")
    .required("First name is required")
    .max(100, "Maximum 100 characters allowed"),
  lastName: yup
    .string()
    .trim()
    .matches(RegularExpressions.Name, "Invalid Last Name")
    .required("Last name is required")
    .max(100, "Maximum 100 characters allowed"),
  middleName: yup
    .string()
    .trim()
    .matches(RegularExpressions.Name, "Invalid Middle Name")
    .max(50, "Maximum 50 characters allowed")
    .nullable(),
  suffix: yup
    .string()
    .trim()
    .max(100, "Maximum 100 characters allowed")
    .nullable(),
  ssn: yup
    .string()
    .trim()
    .required("SSN is required")
    .test(
      "ssn-length",
      "SSN should be of 9 digits",
      (value) => value && value.length === 9
    ),
  hireDate: isValidDateTest("Hire Date"),
});

const addFields = [
  { label: "Employee ID", name: "employeeNum", required: true },
  {
    name: "suffix",
    label: "Suffix",
    type: "autocomplete",
    md: 2.4,
  },
  { label: "Last Name", name: "lastName", required: true },
  { label: "First Name", name: "firstName", required: true },
  { label: "Middle Name", name: "middleName" },
  {
    label: "SSN",
    name: "ssn",
    type: "text",
    InputLabelProps: { shrink: true },
    required: true,
  },
];

const EmployeeAddForm = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const userAgency = useSelector((state) => state.user.userAgency);
  const [defaultEmploymentStatus, setDefaultEmploymentStatus] = useState(null);
  const [autoGenerateID, setAutoGenerateID] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const dictionary = useSelector((state) => state.dictionary.dictionary);
  const suffixOptions = mapDictionaryOptions(dictionary.codeNameSuffixes);

  const {
    control,
    setValue,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      employeeNum: "",
      suffix: "",
      lastName: "",
      firstName: "",
      middleName: "",
      ssn: "",
      hireDate: "",
    },
  });

  const fetchEmploymentStatus = async () => {
    const employmentStatusService = getService("code-per-employment-status");
    const result = await employmentStatusService.find({
      query: { $limit: -1, isDeleted: false, agencyId: userAgency },
    });
    const defaultRecord = result.find((item) => item.isDefault);
    if (defaultRecord && defaultRecord.code) {
      setDefaultEmploymentStatus(defaultRecord.code);
    }
  };

  useEffect(() => {
    fetchEmploymentStatus();
  }, [userAgency]);

  const generateCustomId = async () => {
    const data = {
      EntityName: entityNamesForCustomId.personnelEmployeeId,
      agencyID: userAgency,
    };
    const service = getService(serviceNames.customId);
    service.timeout = 20000;

    const id = await service.create(data);
    if (id?.includes("Agency ID does not match")) {
      dispatch(
        notify(
          "Unable to generate Employee ID because the Agency ID does not match.",
          "warning"
        )
      );
      return null;
    }

    return id;
  };

  useEffect(() => {
    const fetchAndSetId = async () => {
      if (autoGenerateID) {
        const customId = await generateCustomId();
        if (customId) {
          setValue("employeeNum", customId);
        }
      }
    };
    fetchAndSetId();
  }, [autoGenerateID, setValue]);

  const onSubmit = async (data) => {
    const cleanedData = {
      ...data,
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      middleName: data.middleName ? data.middleName.trim() : null,
      suffix: data.suffix ? data.suffix.trim() : null,
      hireDate: data.hireDate
        ? formatSaveDate(data.hireDate, "MM/DD/YYYY")
        : null,
      ssn: data.ssn.trim(),
      employeeNum: data.employeeNum.trim(),
      status: defaultEmploymentStatus,
      agencyId: userAgency,
    };

    dispatch(createEmployee(cleanedData)).then((result) => {
      if (result?.duplicate) {
        dispatch(
          notify(
            `Employee Number "${cleanedData.employeeNum}" already exists.`,
            "warning"
          )
        );
        return;
      }

      if (result?.id) {
        history.push(`${PATH_EMPLOYEE_EDIT}${result.id}`);
      } else {
        history.push(`${PATH_EMPLOYEE}`);
      }
    });
  };
  const employeeNum = watch("employeeNum");
  const handleAutoGenerateClick = async () => {
    if (employeeNum) {
      setOpenDialog(true);
    } else {
      const id = await generateCustomId();
      if (id) {
        setValue("employeeNum", id);
      }
    }
  };

  const generateEmployeeId = async () => {
    setOpenDialog(false);

    const id = await generateCustomId();
    if (id) {
      setValue("employeeNum", id);
    }
  };

  return (
    <>
      <Dialog open={true} fullWidth maxWidth="md">
        <DialogTitle>Add Employee</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              position: "relative",
              border: "1px solid #1e8dfa",
              borderRadius: "10px",
              padding: 3,
              mt: 4,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -18,
                left: 16,
                backgroundColor: "#1e8dfa",
                color: "white",
                px: 2,
                py: "2px",
                borderRadius: "10px",
                fontWeight: "bold",
              }}
            >
              Personal Information
            </Box>
            <Grid container spacing={2}>
              {addFields.map((field) => {
                if (field.name === "employeeNum") {
                  return (
                    <Grid item xs={12} sm={6} key={field.name}>
                      <Box display="flex" alignItems="center">
                        <Box flexGrow={1}>
                          <TextFieldController
                            name={field.name}
                            label={field.label}
                            required={field.required}
                            control={control}
                            errors={errors}
                            disabled={autoGenerateID}
                          />
                        </Box>
                        <Box>
                          <Tooltip title="Auto generate employee number">
                            <IconButton
                              color="primary"
                              aria-label="Auto generate employee number"
                              size="large"
                              onClick={handleAutoGenerateClick}
                            >
                              <AssignmentReturn />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Grid>
                  );
                }
                return (
                  <Grid item xs={12} sm={6} key={field.name}>
                    {field.type === "autocomplete" ? (
                      <DropdownController
                        name={field.name}
                        label={field.label}
                        options={suffixOptions}
                        control={control}
                        errors={errors}
                      />
                    ) : (
                      <TextFieldController
                        name={field.name}
                        label={field.label}
                        type={field.type}
                        required={field.required}
                        control={control}
                        errors={errors}
                        InputLabelProps={field.InputLabelProps}
                        isSSN={field.name === "ssn"}
                      />
                    )}
                  </Grid>
                );
              })}

              <Grid item xs={12} sm={6}>
                <DatePickerController
                  name="hireDate"
                  label="Hire Date"
                  control={control}
                  errors={errors}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Box display="flex" gap={2}>
            <PATSaveButton
              variant="contained"
              color="primary"
              onClick={handleSubmit(onSubmit)}
            />
            <PATCloseButton
              tooltipText="Close"
              onClick={() => history.push(`${PATH_EMPLOYEE}`)}
            />
          </Box>
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onConfirm={() => generateEmployeeId()}
        title="Confirmation"
        content="Are you sure you want to replace the existing employee number?"
      />
    </>
  );
};

export default EmployeeAddForm;
